import { hasOwnProperty } from "@glideapps/ts-necessities";
import { TableName } from "common/database-strings";
import { IconName } from "common/named-icons";
import React from "react";

export type ItemPath = readonly number[];

export type Separator = null;
export const separator: Separator = null;

export function isSeparator(x: unknown): x is Separator {
    return x === separator;
}

export type ItemsType<T> = T | Group<T> | Separator;

export interface Group<T> {
    readonly kind: "group";
    readonly name: string;
    readonly items: readonly ItemsType<T>[];
    readonly hint?: string;
    readonly icon?: string | React.ReactNode;
    readonly isEnabled?: boolean;
}

export type VisualStyle = "normal" | "destructive" | "warning";

export interface ItemDescription {
    readonly name: string;
    readonly wrapName?: boolean;
    readonly selectedName?: string;
    readonly hint?: string;
    readonly accelerator?: [string] | [string, string];
    readonly icon?: string | React.ReactNode | IconName;
    readonly selectedIcon?: string | React.ReactNode | IconName;
    readonly actionIcon?: string | React.ReactNode | IconName;
    readonly overlayIcon?: IconName;
    readonly columnWritten?: [TableName, string];
    readonly isEnabled?: boolean;
    readonly layoutStyle?: "horizontal" | "vertical";
    readonly visualStyle?: VisualStyle;
}

export interface FlatSortArg<T> {
    readonly item: T;
    readonly path: readonly Group<T>[];
}

export interface BaseDropdownProps<T extends {}> {
    readonly items: readonly ItemsType<T>[];
    readonly className?: string;
    readonly selected?: T;
    readonly prelight?: T | Group<T> | ItemPath;
    readonly keyboardListenerMode?: "default" | "none" | "navigation" | "accelerators";
    readonly width?: number;
    readonly height?: number;
    readonly searchable?: boolean;

    // callbacks
    readonly descriptionForItem: (item: T) => ItemDescription | string;
    readonly flatSort?: (a: FlatSortArg<T>, b: FlatSortArg<T>) => number;

    // events
    readonly onItemClicked?: (item: T, path: ItemPath, e: React.MouseEvent) => void;
    readonly onItemActionClicked?: (item: T, path: ItemPath, e: React.MouseEvent) => void;
    readonly onGroupClicked?: (group: Group<T>, path: ItemPath, e: React.MouseEvent) => void;
    readonly onItemDidPrelight?: (item: T | undefined, path: ItemPath | undefined) => void;
    readonly onItemWillPrelight?: (item: T | Group<T> | undefined, path: ItemPath | undefined) => void;
    readonly onItemSelect?: (item: T, path: ItemPath) => void;
}

export interface InnerItemDescription<T> extends Omit<ItemDescription, "accelerator"> {
    readonly item: T | Group<T>;
    readonly accelerator?: string;
    readonly lineage?: readonly InnerItemDescription<T>[];
    readonly prelight: boolean;
    readonly containsPrelight: boolean;
    readonly prelighting: boolean;
    readonly containsPrelighting: boolean;
    readonly isSelected: boolean;
    readonly children?: readonly (InnerItemDescription<T> | null)[];
    readonly indexPath: readonly number[];
}

export function isGroup<T>(obj: ItemsType<T> | undefined): obj is Group<T> {
    return (
        obj !== undefined &&
        obj !== null &&
        hasOwnProperty(obj, "kind") &&
        obj.kind === "group" &&
        typeof obj.name === "string"
    );
}

export function isValue<T>(obj: ItemsType<T> | undefined): obj is T {
    return obj !== undefined && obj !== null && !isGroup(obj);
}

export function findPathInGroup<T>(root: readonly ItemsType<T>[], needle: T): readonly (T | Group<T>)[] | undefined {
    const walk = (toWalk: readonly ItemsType<T>[]): readonly (T | Group<T>)[] | undefined => {
        for (const i of toWalk) {
            if (i === null) continue;
            if (i === needle) return [needle];

            if (isGroup(i)) {
                const walked = walk(i.items);
                if (walked !== undefined) {
                    return [i, ...walked];
                }
            }
        }

        return undefined;
    };

    return walk(root);
}

export function pointIsInShadow(
    start: [number, number],
    current: [number, number],
    throat: number,
    slopeDeg: number,
    deadZone: number = 0
) {
    const [x1, y1] = start;
    const [x2, y2] = current;
    const s = throat;
    const alpha = slopeDeg * (Math.PI / 180);
    const w = deadZone;
    const d = Math.abs(x2 - x1);
    const h = s / 2 + Math.tan(alpha) * d;
    return Math.abs(y2 - y1) <= h && d >= w;
}
